import React, { useEffect, useState } from 'react';
import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

import { BreadcrumbBar } from '..';

import { HeaderSection } from './HeaderSection';
import { buildImageUrl, formatDistance, getWithLocale } from '../../utils/utils';
import { useDetail } from '../../pages_handler/listPage/client-side/DetailContext';
import { GalleryView } from '../galleryView';
import { DetailMenuSection } from './DetailMenuSection';
import { SubcateLinksDetail } from '../SubcateLinksDetail';
import { buildProjectHref, ProjectCard } from '../projectSlider';
import dynamic from 'next/dynamic';
const DynamicPropertySlider = dynamic(() => import('../propertySlider'), {
  ssr: false,
});
const DynamicProjectSlider = dynamic(() => import('../projectSlider'), {
  ssr: false,
});
const DynamicLeaveYourRequirement = dynamic<any>(
  () => import('../../components/').then((module) => module.LeaveYourRequirement),
  {
    ssr: false,
  },
);

const DynamicSearchBar = dynamic(() => import('../../components/SearchBarHome'), { ssr: false });
const DynamicCategoriesCarousel = dynamic<any>(() => import('../../components/CategoriesCarousel'), {
  ssr: false,
});

interface NearbyLocation {
  distance: string;
  location: string;
}

export interface NearbyItem {
  id: number | string;
  image: string;
  title: string;
  price?: number;
  rentPrice?: number;
  bedroom?: number;
  bathroom?: number;
  area?: number;
  projectLogo: string;
  project: string;
  listingTitle: string;
  listingDescription: string;
  location: string;
  nearbyLocation: string;
  tags: string[];
}

export const DetailPageLayout = ({
  action,
  t,
  breadcrumb,
  header,
  detail,
  data,
  nearbyList,
  locale,
  categoryId,
  actionId,
  nearbyPropertyList,
  nearbyProjectList,
}) => {
  const [nearbyLocation, setNearbyLocation] = useState<NearbyLocation>({ distance: '', location: '' });
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const paddingContainer = isMobile ? 1 : isTablet ? 5 : 0;

  const { sectionList, sectionRefs } = useDetail();
  const [activeKey, setActiveKey] = useState(() => sectionList?.[0]?.key || '');
  const isProject = action === t('project');

  const handleMenuClick = (key) => {
    setActiveKey(key);
    sectionRefs.current[key]?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const getSafeLanguageKey = (locale?: string) => {
    const lang = locale?.toUpperCase?.();
    return ['TH', 'EN', 'CN'].includes(lang || '') ? lang : 'TH';
  };

  const getImagePath = (data: any, galleryPath: string, id: string | number) => {
    const isStaging = Boolean(
      data?.images?.startsWith('staging/') || data?.images?.startsWith('https://cdn.9asset.com/'),
    );

    return buildImageUrl({
      galleryPath,
      pathId: isStaging ? undefined : id,
      fileName: isStaging ? undefined : data?.images,
      fullPath: isStaging ? data?.images : undefined,
      isDesktop: false,
      width: 420,
      height: 230,
      quality: 50,
    });
  };

  const getLocalizedValue = (obj: any, lang: string) => {
    if (!obj || typeof obj !== 'object') return '';

    const langs = ['TH', 'EN', 'CN'];
    const validLang = langs.includes(lang) ? lang : 'TH';

    return obj[validLang] ?? obj.TH ?? obj.EN ?? obj.CN ?? '';
  };

  const getLocation = (itemData: any, lang: string): string => {
    return [
      getLocalizedValue(itemData?.district?.name, lang),
      getLocalizedValue(itemData?.amphur?.name, lang),
      getLocalizedValue(itemData?.province?.name, lang),
    ]
      .filter(Boolean)
      .join(', ');
  };

  const mapNearbyItemProperty = (itemData: any, galleryPath: string, languageKey: string) => {
    const listingTitle = getLocalizedValue(itemData?.property?.name, languageKey);
    const projectName = getLocalizedValue(itemData?.project?.name, languageKey);
    const location = getLocation(itemData, languageKey);
    const imageUrl = getImagePath(itemData.property, galleryPath, itemData.property?.id);

    const getActionCategory = (property, project) => {
      const actionMap = [
        { key: 'sell', value: property?.sell },
        { key: 'rent', value: property?.rent },
        { key: 'lease', value: property?.price_lease },
        { key: 'mortgage', value: property?.price_mortgage },
        { key: 'redemption', value: property?.price_redemption },
      ];

      const actionKey = actionMap.find((item) => Number(item.value) > 0)?.key || '';
      const action = actionKey ? t(actionKey) : '';

      const category = getLocalizedValue(project?.category?.name, languageKey);
      return { action, category };
    };

    const actionCategory = getActionCategory(itemData.property, itemData.project);
    const title = actionCategory.action + actionCategory.category;

    return {
      id: itemData.property?.id,
      image: imageUrl,
      title: title,
      action: actionCategory.action,
      category: actionCategory.category,
      price: itemData.property?.sell,
      bedroom: itemData.property?.bedroom,
      bathroom: itemData.property?.bathroom,
      area: itemData.property?.property_size,
      projectLogo: itemData.project?.logoImage || imageUrl,
      project: projectName,
      listingTitle: listingTitle,
      listingDescription: itemData.property?.additional,
      location: location,
      nearbyLocation: itemData.property?.nearLocation,
      tags: itemData.property?.specail, // status (special)
    };
  };

  const mapNearbyItemProject = (itemData: any, galleryPath: string, languageKey: string) => {
    const projectName = getLocalizedValue(itemData.project?.name, languageKey);
    const projectCategory = getLocalizedValue(itemData.project?.category?.name, languageKey);
    const location = getLocation(itemData, languageKey);
    const imageUrl = getImagePath(itemData.project, galleryPath, itemData.project?.id);

    return {
      id: itemData.project?.id,
      image: imageUrl,
      title: projectName,
      category: projectCategory,
      price: itemData.project?.price,
      rentPrice: itemData.project?.price_rent_min,
      bedroom: itemData.project?.bedroom,
      bathroom: itemData.project?.bathroom,
      area: itemData.project?.size,
      location: location,
      nearbyLocation: itemData.project?.nearLocation,
      tags: itemData.project?.specail, // status (special)
      countSell: itemData.project?.property_count_sell,
      countRent: itemData.project?.property_count_rent,
    };
  };

  const languageKey = getSafeLanguageKey(locale);

  const properties = nearbyPropertyList?.map((list) =>
    mapNearbyItemProperty(list, 'property_gallery', languageKey || ''),
  );

  const projects = nearbyProjectList?.map((list) => mapNearbyItemProject(list, 'project_image', languageKey || ''));

  useEffect(() => {
    if (!nearbyList?.length || !locale) return;

    const item = nearbyList?.find((i) => i?.children?.props?.data?.[0]);
    if (!item) {
      setNearbyLocation({ distance: '', location: '' });
      return;
    }

    const data = item.children?.props?.data[0];
    setNearbyLocation({
      distance: formatDistance(Number(data?.distance), locale),
      location: `${data?.location_type_ID != 10 ? getWithLocale(data, 'Name', locale) : ''} ${getWithLocale(
        data,
        'branch_Name',
        locale,
      )}`,
    });
  }, [nearbyList, locale]);

  return (
    <Box sx={{ bgcolor: 'common.white' }}>
      {/* Sticky SearchBar */}
      {!isMobile && (
        <Box
          maxWidth="1200px"
          sx={{
            mt: 15,
            width: '100%',
            position: isTablet ? 'static' : 'sticky',
            top: 10,
            left: 0,
            zIndex: 9999,
            mx: 'auto',
            px: paddingContainer,
          }}
        >
          <DynamicSearchBar />
        </Box>
      )}

      {/* Top Bar Section: Categories + Breadcrumb */}
      <Box
        maxWidth="lg"
        sx={{
          mt: 5,
          mb: 1,
          width: '100%',
          mx: 'auto',
          px: paddingContainer,
        }}
      >
        <DynamicCategoriesCarousel />
        <Box>
          <BreadcrumbBar breadcrumb={breadcrumb} locale={locale} t={t} />
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ py: 7, bgcolor: '#F5F5F6' }}>
        <Box maxWidth="lg" margin="auto" sx={{ px: paddingContainer }}>
          {/* Gallery + Header */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              bgcolor: '#FFF',
              borderRadius: '20px',
              mb: 2,
              gap: 2,
            }}
          >
            <GalleryView t={t} images={data?.images} projectId={data?.project_ID} propertyId={data?.property_ID} />
            <Box p={{ md: 1 }} py={2} pb={{ md: 3 }}>
              <HeaderSection header={header} nearbyLocation={nearbyLocation} isProject={isProject} detail={data} />
            </Box>
          </Box>

          <Box display={'flex'} justifyContent={'space-between'}>
            <Box flex="1 1 0" minWidth={0}>
              {/* Menu Detail */}
              <Box
                sx={{
                  bgcolor: '#FFF',
                  borderRadius: 4,
                  px: { xs: 1, md: 2 },
                  py: 0,
                }}
              >
                <DetailMenuSection sectionList={sectionList} activeKey={activeKey} onMenuClick={handleMenuClick} />
              </Box>

              {/* Main Detail */}
              <Box
                sx={{
                  bgcolor: '#FFF',
                  borderRadius: '20px',
                  px: { xs: 1.5, md: 3 },
                  py: 2,
                  mt: 2,
                }}
              >
                {{ ...detail, sectionRefs: sectionRefs }}
              </Box>

              <DynamicPropertySlider title={t('property-recommended')} properties={properties} locale={locale} />
              <DynamicProjectSlider title={t('project-nearby')} projects={projects} locale={locale} />
            </Box>

            {/* Sidebar (Desktop only) */}
            <Box
              sx={{
                display: { xs: 'none', lg: 'flex' },
                flexDirection: 'column',
                gap: 2,
                width: 280,
                height: 'auto',
                ml: 2,
              }}
            >
              <DynamicLeaveYourRequirement t={t} />

              {projects?.length > 0 && (
                <Stack gap={2}>
                  <Typography variant="h3" fontSize="1rem" fontWeight={500}>
                    {t('project-nearby')}
                  </Typography>
                  {projects.slice(0, 2).map((proj) => (
                    <ProjectCard
                      key={proj.id}
                      project={proj}
                      onClick={() => window.open(buildProjectHref(proj, locale, t), '_self')}
                      mx={0}
                    />
                  ))}
                </Stack>
              )}

              <SubcateLinksDetail
                t={t}
                actionid={actionId}
                categoryid={categoryId}
                locale={locale}
                searchParamsList={{}}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
